import toast from 'react-hot-toast';
import { NavigateFunction } from 'react-router-dom';

interface UploadFileOptions {
  endpoint: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  successMessage?: string;
  errorMessage?: string;
  navigate?: NavigateFunction;
  navigateTo?: string;
}

// For createUploadHandler, we use a separate interface where all fields can be optional
interface PartialUploadFileOptions extends Partial<UploadFileOptions> {}

/**
 * A reusable function to handle file uploads to different endpoints
 *
 * @param file - The file to upload
 * @param setUploading - State setter for upload status
 * @param options - Configuration options for the upload
 */
export const uploadFile = async (file: File, setUploading: (isUploading: boolean) => void, options: UploadFileOptions) => {
  const {
    endpoint,
    onSuccess,
    onError,
    successMessage = 'Berhasil Upload',
    errorMessage = 'Terjadi kesalahan ketika fetch.',
    navigate,
    navigateTo,
  } = options;

  setUploading(true);

  const formData = new FormData();
  formData.append('file', file);

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      body: formData,
    });

    const res = await response.json();

    if (!response.ok) {
      throw new Error(res.errors?.[0]?.message || res.message);
    }

    // Show success toast
    if (toast) {
      toast.success(successMessage);
    } else {
      alert(successMessage);
    }

    // Call success callback if provided
    if (onSuccess) {
      onSuccess();
    }

    // Navigate if needed
    if (navigate && navigateTo) {
      navigate(navigateTo);
    }

    return res;
  } catch (error: any) {
    // Show error toast
    const errorMsg = error.message || errorMessage;

    if (toast) {
      toast.error(errorMsg);
    } else {
      alert(`Error: ${errorMsg}`);
    }

    // Call error callback if provided
    if (onError) {
      onError(error);
    }

    throw error;
  } finally {
    setUploading(false);
  }
};

/**
 * Create a file upload handler with predefined settings
 *
 * @param setUploading - State setter for upload status
 * @param options - Default options for this upload handler
 */
export const createUploadHandler = (
  setUploading: (isUploading: boolean) => void,
  defaultOptions: PartialUploadFileOptions = {}
) => {
  return (file: File, options: PartialUploadFileOptions = {}) => {
    // Merge options and ensure endpoint exists before calling uploadFile
    const mergedOptions = {
      ...defaultOptions,
      ...options,
    };

    // Validate that we have the required endpoint
    if (!mergedOptions.endpoint) {
      throw new Error('No endpoint specified for file upload');
    }

    // Now we can safely call uploadFile as we've validated mergedOptions has an endpoint
    return uploadFile(file, setUploading, mergedOptions as UploadFileOptions);
  };
};
